import {
  parseKaraokeDataFromLegacyTranscriptElements,
  shiftLegacyTranscriptAttributesFromWhitespaces,
} from '@src/editor/services/legacyKaraokeImportExport';
import { EditorChangeType } from '@src/editor/types';
import { ActionEventType, ActionType } from '@src/monitoring/types';
import { MAX_RECORD_SIZE } from '@src/repositories';
import { KARAOKE_TRACK_NUMBER } from '@src/stores/VideoCreatorStore';
import { useVideoCreatorStore } from '@src/stores-v2';
import {
  useDatoClient,
  useStore,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { LogManager } from '@src/utility/Logger';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { reaction } from 'mobx';
import { useCallback, useEffect, useRef } from 'react';

import { extractErrorInfo } from '../utility/error';
import { useVideoEditorLogging } from './useVideoEditorLogging';

import type { EditorObserver } from '@src/editor/types';
import type { VideoSource } from '@src/stores-v2';
import type {
  TranscriptElementLegacy,
  TranscriptElementWithClipSegmentAttributes,
} from '@src/types.ts/transcript';

const logger = LogManager.getLogger('hook:useHeadlessEditorIntegration');

let reloadSourcesExecutionCount = 0;
let reloadSourcesCallCount = 0;

function hasKaraokeTrackInSource(videoSource: VideoSource) {
  return !!videoSource.elements?.find(el => el.track === KARAOKE_TRACK_NUMBER);
}

/**
 * Hook that wires the headless `EditorStore` to the global `VideoSourceStore`.
 *
 *  • On mount it loads any existing `videoSourceStore.source` into the editor
 *    (instant).
 *  • It observes timeline / element changes in the editor and instantly pushes
 *    the translated source to `videoSourceStore.setSource`.
 *  • It reacts to `videoSourceStore.onSourceChange` to immediately sync any
 *    incoming source changes into the editor (unless we were the originator of
 *    the change).
 *
 * The `VideoSourceStore` itself is still responsible for debouncing writes to
 * the renderer, so we purposefully do *not* debounce here.
 */
export function useHeadlessEditorIntegration(
  enableDebugTools: boolean = true,
  enableLogging: boolean = typeof process !== 'undefined' &&
    process.env.NODE_ENV === 'development',
) {
  const {
    editorStore,
    videoSourceStore,
    transcriptionStore,
    karaokeStore,
    analyticsStore,
  } = useStore();
  const flagsInitialized = useRef(false);
  const videoCreator = useVideoCreatorStore();
  const datoClient = useDatoClient();

  // Set up VideoEditor logging if enabled
  useVideoEditorLogging(
    editorStore?.editor,
    {
      enableCommandLogging: enableLogging,
      enableBatchLogging: enableLogging,
      enablePerformanceLogging: enableLogging && enableDebugTools,
      detailedCommandPerformance: enableDebugTools,
    },
    enableLogging,
  );

  // ---------------------------------------------------------------------------
  // Feature flag – default to `true` for local / dev environments
  // ---------------------------------------------------------------------------
  const { currentRole } = useUserIdentity();
  const flags = useFlagsCombination(currentRole);

  const {
    initialized,
    enableHeadlessEditor = true,
    enableHeadlessEditorExperimental = true,
    enableHeadlessEditorWave3 = true,
    enableHeadlessEditorWave4 = true,
    enableTranscriptSegmentArchitecture = false,
    enableTranscriptDualWrite = false,
    enableTranscriptMigrationSizeLimit = true,
    enableTranscriptSnapshotResourceUpload = false,
    enableVideoResourcesS3Upload = false,
    enableNewKaraokeArchitecture = false,
    enableKaraokeDualWrite = false,
  } = flags;

  const isLocalChange = useRef(false);
  const editorRef = useRef<typeof editorStore | null>(
    enableHeadlessEditor ? editorStore : null,
  );

  const reloadSources = useCallback(
    (videoSourceJson: VideoSource, forceReloadTranscript: boolean) => {
      const startTime = performance.now();
      reloadSourcesExecutionCount++;

      logger.info(
        `reloadSources execution #${reloadSourcesExecutionCount} started (calls total: ${reloadSourcesCallCount})`,
      );

      if (!transcriptionStore.isTranscriptionReady) {
        logger.debug('reloading source: transcription not ready, skipping');
        const endTime = performance.now();
        logger.info(
          `reloadSources execution #${reloadSourcesExecutionCount} completed in ${(
            endTime - startTime
          ).toFixed(2)}ms (skipped - transcription not ready)`,
        );
        return;
      }
      const initialMigration =
        editorStore.useTranscriptSegments &&
        !editorStore.transcriptSegmentArchitectureMigrationLog;

      // Present canonical view to headless editor if reframing is active to avoid scaled contamination
      let sourceForEditor = videoSourceJson;
      if (videoCreator.reframingModeManager.isModeActive()) {
        try {
          sourceForEditor =
            videoCreator.reframingModeManager.restoreVideoSourceForExit(
              videoSourceJson,
            );
        } catch (err) {
          logger.error(
            'reframing: restoreVideoSourceForExit failed',
            extractErrorInfo(err),
          );
        }
      }

      editorStore.loadProject(sourceForEditor);

      if (
        !forceReloadTranscript &&
        editorStore.editor.getVideoTranscriptTrack()
      ) {
        const endTime = performance.now();
        logger.debug(
          `re-loading sources: #${reloadSourcesExecutionCount} completed in ${(
            endTime - startTime
          ).toFixed(2)}ms (loaded ${videoSourceJson.elements
            ?.length} elements, skipped transcription)`,
        );
        return;
      }

      const originalTranscription =
        transcriptionStore.getOriginalTranscription();
      let projectTranscription: TranscriptElementLegacy[] =
        transcriptionStore.getFinalTranscriptionElements();

      logger.debug(
        `re-loading sources: ${videoSourceJson.elements?.length} elements, ${projectTranscription.length} transcription items, initialMigration: ${initialMigration}`,
      );

      if (editorStore.enableTranscriptMigrationSizeLimit) {
        const estimateRecordSize =
          datoClient.videoRepository.estimateRecordSize({
            transcriptionSnapshot: {
              elements: originalTranscription?.elements || [],
            },
          });
        if (estimateRecordSize > 0.9 * MAX_RECORD_SIZE) {
          logger.warn(
            'Transcription is too large to store as snapshot, disabling segment architecture',
          );
          // disable until storing snapshots in S3 implemented due to size limits
          editorStore.transcriptSegmentArchitectureNotSupported = true;
          editorStore.transcriptSegmentArchitectureMigrationLog = {
            status: 'not_supported',
          };
        }
      } else {
        logger.debug('Transcript migration - size limit disabled');
      }

      if (
        !karaokeStore.getKaraokeData() &&
        hasKaraokeTrackInSource(videoSourceJson)
      ) {
        projectTranscription =
          shiftLegacyTranscriptAttributesFromWhitespaces(projectTranscription);
      }

      const transcriptLoaded = editorStore.loadTranscript(
        projectTranscription,
        originalTranscription?.elements || [],
      );

      // Track loadTranscript success during initial migration
      if (initialMigration && videoCreator.currentVideoLoadingWorkflowId) {
        const actionId = analyticsStore.startUserAction(
          ActionEventType.LOAD_TRANSCRIPT,
          ActionType.WORKFLOW,
          videoCreator.currentVideoLoadingWorkflowId,
          undefined,
          { transcriptLoaded, initialMigration },
        );
        if (transcriptLoaded) {
          analyticsStore.completeUserAction(actionId);
        } else {
          analyticsStore.failUserAction(actionId, 'Failed to load transcript');
        }
      }

      if (transcriptLoaded && editorStore.useTranscriptSegments) {
        if (editorStore.enableTranscriptDualWrite) {
          videoCreator.videoTranscriptionProcessor.setTranscriptionSnapshot(
            editorStore.editor.getFullTranscript() as TranscriptElementWithClipSegmentAttributes[],
          );
        } else {
          transcriptionStore.updateFromProcessor(
            editorStore.editor.getFullTranscript() as TranscriptElementWithClipSegmentAttributes[],
          );
        }
      }

      // how to name KaraokeClips with KaraokeConfigs in one structure like {clips: KaraokeClip[], configs: KaraokeConfig}
      let karaokeData = karaokeStore.getKaraokeData();
      let karaokeLoaded: boolean | null = null;
      logger.debug('loading karaoke data', { karaokeData });

      if (
        !karaokeData &&
        hasKaraokeTrackInSource(videoSourceJson) &&
        karaokeStore.legacyKaraokeConfigLoaded
      ) {
        const migratedConfig = editorStore.editor.createKaraokeConfig(
          karaokeStore.legacyKaraokeConfigLoaded,
        );
        karaokeData = parseKaraokeDataFromLegacyTranscriptElements(
          editorStore.editor.getFullTranscript(),
          migratedConfig,
        );
        logger.debug('parsed karaoke data from legacy transcript');
      }

      if (karaokeData) {
        karaokeLoaded = editorStore.loadKaraokeData(karaokeData);
      } else {
        karaokeLoaded = editorStore.editor.initializeDefaultKaraokeData();

        if (initialMigration && videoCreator.currentVideoLoadingWorkflowId) {
          // Track absence of karaokeData during initial migration
          const actionId = analyticsStore.startUserAction(
            ActionEventType.LOAD_KARAOKE_DATA,
            ActionType.WORKFLOW,
            videoCreator.currentVideoLoadingWorkflowId,
            undefined,
            { defaultKaraokeDataInitialized: true, initialMigration },
          );
          analyticsStore.completeUserAction(actionId, {
            defaultKaraokeDataInitialized: true,
          });
        }
      }

      if (editorStore.useNewKaraoke && karaokeLoaded) {
        karaokeStore.syncFromEditor(editorStore.editor);

        // Track loadKaraokeData success during initial migration
        if (initialMigration && videoCreator.currentVideoLoadingWorkflowId) {
          const actionId = analyticsStore.startUserAction(
            ActionEventType.LOAD_KARAOKE_DATA,
            ActionType.WORKFLOW,
            videoCreator.currentVideoLoadingWorkflowId,
            undefined,
            { karaokeLoaded, initialMigration },
          );
          if (karaokeLoaded) {
            analyticsStore.completeUserAction(actionId);
          } else {
            analyticsStore.failUserAction(
              actionId,
              'Failed to load karaoke data',
            );
          }
        }
      }

      if (
        initialMigration &&
        editorStore.transcriptSegmentArchitectureMigrationLog
      ) {
        // Track transcript segment architecture migration log result
        if (videoCreator.currentVideoLoadingWorkflowId) {
          const migrationLog =
            editorStore.transcriptSegmentArchitectureMigrationLog;
          const actionId = analyticsStore.startUserAction(
            ActionEventType.TRANSCRIPT_SEGMENT_ARCHITECTURE_MIGRATION,
            ActionType.WORKFLOW,
            videoCreator.currentVideoLoadingWorkflowId,
            undefined,
            { migrationLog, initialMigration },
          );

          if (migrationLog.status === 'ok') {
            analyticsStore.completeUserAction(actionId, { migrationLog });
          } else {
            analyticsStore.failUserAction(
              actionId,
              `Migration result: ${migrationLog.status}`,
              { migrationLog },
            );
          }
        }

        if (videoCreator.currentVideo?.id) {
          // only if video already exists, saveCurrentAndParentVideos will save migration log as part of video record
          videoCreator.saveCurrentAndParentVideos(false, false, true); // await?
        }
      }

      const endTime = performance.now();
      const duration = endTime - startTime;
      logger.info(
        `reloadSources execution #${reloadSourcesExecutionCount} completed in ${duration.toFixed(
          2,
        )}ms - transcriptLoaded: ${transcriptLoaded} karaokeLoaded: ${karaokeLoaded}`,
      );
    },
    [
      transcriptionStore.isTranscriptionReady,
      editorStore,
      datoClient,
      videoCreator,
      transcriptionStore,
    ],
  );

  const setupHeadlessIntegration = () => {
    // If the feature is disabled simply bail out (but keep hook registered!)
    if (!enableHeadlessEditor) {
      // Drop any debug globals from a previous enabled run
      if (enableDebugTools && typeof window !== 'undefined') {
        delete (window as any).__headlessEditor;
        delete (window as any).__videoSourceStore;
      }
      return;
    }

    // Track setupHeadlessIntegration event
    if (videoCreator.currentVideoLoadingWorkflowId) {
      analyticsStore.startUserAction(
        ActionEventType.SETUP_HEADLESS_INTEGRATION,
        ActionType.WORKFLOW,
        videoCreator.currentVideoLoadingWorkflowId,
      );
    }

    editorStore.headlessEnabled = true;
    // 1️⃣  Initial load – sync existing source into the editor
    if (
      videoSourceStore.source?.elements?.length &&
      !editorStore.preventHeadlessSync &&
      transcriptionStore.isTranscriptionReady
    ) {
      reloadSources(videoSourceStore.source, true);
    }

    // 2️⃣  Headless ➜ Store observer
    const observer: EditorObserver = {
      onEditorChange: (_editor, changeType) => {
        logger.info('OnEditorChange:', changeType);

        if (
          changeType === EditorChangeType.TIMELINE_CHANGED ||
          changeType === EditorChangeType.ELEMENT_ADDED ||
          changeType === EditorChangeType.ELEMENT_UPDATED ||
          changeType === EditorChangeType.ELEMENT_REMOVED
        ) {
          if (isLocalChange.current) return;

          try {
            isLocalChange.current = true;

            const newSource = editorStore.timeline.toJSON();

            // Preserve fonts from the current source
            const currentFonts = videoSourceStore.source?.fonts;
            const sourceWithFonts = currentFonts
              ? { ...newSource, fonts: currentFonts }
              : newSource;

            // If reframing active, project canonical edits back to reframed preview before persisting
            const finalSource = videoCreator.reframingModeManager.isModeActive()
              ? videoCreator.reframingModeManager.modifyVideoSourceForEnter(
                  sourceWithFonts,
                  editorStore.useNewKaraoke,
                )
              : sourceWithFonts;

            // Lightweight guard – prevent needless writes when the element
            // signature (ids + length) hasn’t changed.
            const prevElements = videoSourceStore.source?.elements ?? [];
            const nextElements = newSource.elements ?? [];

            const sameSignature =
              prevElements.length === nextElements.length &&
              prevElements.every((el: any, idx: number) =>
                el?.id && nextElements[idx]?.id
                  ? el.id === nextElements[idx].id
                  : true,
              );

            if (!sameSignature) {
              videoSourceStore.setSource(finalSource);
            } else {
              // Even if signature is the same, timing or other props may have
              // changed – still push the update for simplicity.
              videoSourceStore.setSource(finalSource);
            }

            if (
              editorStore.useTranscriptSegments &&
              !editorStore.preventHeadlessSync
            ) {
              const editorFullTranscript =
                editorStore.editor.getFullTranscript();

              logger.debug(
                'headless -> store observer: updating transcription store',
              );
              if (editorStore.enableTranscriptDualWrite) {
                logger.debug(
                  'headless -> store observer: legacy sync: updating VideoTranscriptionProcessor state',
                );
                // updates legacy processor state which also triggers transcription store update
                videoCreator.videoTranscriptionProcessor.setTranscriptionSnapshot(
                  editorFullTranscript as TranscriptElementWithClipSegmentAttributes[],
                );
                // + refresh legacy karaoke to reflect changes until karaoke is migrated
                videoCreator.refreshKaraokeElements();
              } else {
                // only updates transcription store
                transcriptionStore.updateFromProcessor(
                  editorFullTranscript as TranscriptElementWithClipSegmentAttributes[],
                );
              }
            }

            if (editorStore.useNewKaraoke) {
              karaokeStore.syncFromEditor(editorStore.editor); // trying different pattern here making store responsible for pulling data instead of directly setting final output
            }
          } finally {
            isLocalChange.current = false;
          }
        }
      },
    };

    editorStore.editor.registerObserver(observer);

    // 3️⃣  Store ➜ Headless reaction
    const disposeReaction = videoSourceStore.onSourceChange(videoSource => {
      // console.log('useHeadlessIntegration: onSourceChange:', src?.elements);
      if (
        isLocalChange.current ||
        editorStore.preventHeadlessSync ||
        !videoSource.elements?.length
      ) {
        console.log('useHeadlessIntegration: onSourceChange: skipping sync');
        return;
      }

      try {
        isLocalChange.current = true;
        const forceTranscriptLoad = !(
          editorStore.useTranscriptSegments &&
          editorStore.editor.getVideoTranscriptTrack()
        );
        logger.debug('VideoSourceStore -> headless observer: reload sources', {
          forceTranscriptLoad,
        });
        reloadSources(videoSource, forceTranscriptLoad);
        editorStore.refreshTimeline();
      } finally {
        isLocalChange.current = false;
      }
    });

    // Optional legacy listener: only register if provided
    const disposeReaction2 = videoCreator.onExtraElementDataChange
      ? videoCreator.onExtraElementDataChange(extraElementData => {
          editorStore.editor.passExtraElementData(extraElementData);
        })
      : () => {};

    // 4️⃣  Debug helpers (window globals)
    if (enableDebugTools && typeof window !== 'undefined') {
      (window as any).__headlessEditor = editorStore;
      (window as any).__videoSourceStore = videoSourceStore;
    }

    // 🧹  Cleanup
    return () => {
      editorStore.editor.unregisterObserver(observer);
      disposeReaction();
      disposeReaction2();
      // reloadSources.cancel(); // Cancel any pending debounced calls

      if (enableDebugTools && typeof window !== 'undefined') {
        delete (window as any).__headlessEditor;
        delete (window as any).__videoSourceStore;
      }
    };
  };

  useEffect(() => {
    // setup flags once LaunchDarkly initialized and don't update again mid-session (except for enabling new transcript system)
    if (
      initialized &&
      (!flagsInitialized.current ||
        (enableTranscriptSegmentArchitecture &&
          !editorStore.enableTranscriptSegmentArchitecture))
    ) {
      flagsInitialized.current = true;
      editorStore.headlessEnabled = enableHeadlessEditor;
      editorStore.enableNewKaraokeArchitecture = enableNewKaraokeArchitecture;
      editorStore.enableHeadlessEditorExperimental =
        enableHeadlessEditorExperimental;
      editorStore.enableHeadlessEditorWave3 = enableHeadlessEditorWave3;
      editorStore.enableHeadlessEditorWave4 = enableHeadlessEditorWave4;
      editorStore.enableTranscriptSegmentArchitecture =
        enableTranscriptSegmentArchitecture;
      editorStore.enableTranscriptDualWrite = enableTranscriptDualWrite;
      editorStore.enableTranscriptMigrationSizeLimit =
        enableTranscriptMigrationSizeLimit;
      editorStore.enableKaraokeDualWrite =
        enableKaraokeDualWrite && enableNewKaraokeArchitecture;
      editorStore.enableTranscriptDualWrite = enableTranscriptDualWrite;

      // only if transcript snapshot is uploaded to s3 directly, we don't need to limit size of snapshot
      editorStore.enableTranscriptMigrationSizeLimit =
        (!enableTranscriptSnapshotResourceUpload ||
          !enableVideoResourcesS3Upload) &&
        enableTranscriptMigrationSizeLimit;
      logger.info('EditorStore flags initialized:', {
        enableNewKaraokeArchitecture,
        enableTranscriptSegmentArchitecture,
        enableTranscriptDualWrite,
      });
    }
  }, [initialized, enableTranscriptSegmentArchitecture]);

  // Creatomate <-> Stores (MobX - VideoSourceStore, TranscriptStore) <-> VideoEditor
  useEffect(() => {
    if (
      editorStore.headlessEnabled &&
      transcriptionStore.isTranscriptionReady &&
      videoSourceStore.source?.elements?.length &&
      editorStore.useTranscriptSegments
    ) {
      return setupHeadlessIntegration();
    }

    // We intentionally ignore dependencies that would recreate observers on
    // every render.
  }, [
    editorStore.useTranscriptSegments,
    editorStore.headlessEnabled,
    !!videoSourceStore.source?.elements?.length,
    transcriptionStore.isTranscriptionReady,
  ]);

  useEffect(() => {
    // Legacy useEffect - mostly intact
    if (enableHeadlessEditor && !editorStore.useTranscriptSegments) {
      return setupHeadlessIntegration();
    }
    // Legacy effect - on every videoSourceStore.source?.elements change
  }, [
    editorStore.headlessEnabled,
    videoSourceStore.source?.elements,
    transcriptionStore.isTranscriptionReady,
  ]);

  useEffect(() => {
    // Watch for forceReloadTrigger changes and reload sources
    return reaction(
      () => editorStore.forceReloadTrigger,
      () => {
        logger.debug(
          'forceReloadTrigger triggered',
          videoSourceStore.source?.elements?.length,
          enableHeadlessEditor,
        );
        if (!enableHeadlessEditor || !editorStore.forceReloadTrigger) return;

        if (videoSourceStore.source?.elements?.length) {
          logger.debug('forceReloadHeadless triggered');
          reloadSources(videoSourceStore.source, true);
        }
      },
    );
  }, [editorStore.headlessEnabled]);

  return editorRef;
}
